import { getConfig } from '@/utils/config/config';
import { buildApiUrl } from '@/utils/url/build-url';
import { getCookieValue, COOKIES } from '@/utils/cookies';
import { DEVICE, LOCALES } from '@/utils/constants';
import type { Brand, Locale, PrismicLocaleCode } from '@/types/constants';
import type { SearchHit } from '@/lib/search-hit';
import { isMlSearchActive } from '@/lib/ml-search-gate';
import { fetchApi } from './fetcher';

export type { EmbeddedSearchHit, LegacySearchHit, SearchHit, SlimSearchGameData } from '@/lib/search-hit';

export type SearchHitsPayload = {
    total: {
        value: number;
    };
    hits: SearchHit[];
};

export type SearchHits = SearchHitsPayload & {
    searchEngine: string;
    searchEngineVersion: string;
    hasNextPage: boolean;
};

export type SearchResponse = {
    body: {
        hits: SearchHitsPayload;
    };
    metadata: {
        has_next_page: boolean;
    };
    searchEngine: string;
    searchEngineVersion: string;
};

export type SearchOptions = {
    /** Zero-based page index. Passed as offset to the API (pageIndex * pageSize). */
    pageIndex?: number;
    /** Number of results per page. Default 10 (desktop). Use 20 for mobile/tablet. */
    pageSize?: number;
    /** Amplitude expermient value: variant/control */
    experimentGroupId?: string;
    /** Snowplow client_session.sessionId (domain_sessionid). */
    sessionId?: string;
    /** Passed to the lambda as x-request-id when calling the ML search API. */
    requestId?: string;
    /** Amplitude feature flag: user is eligible for ML search (requires variant experiment to route to ML client). */
    hasCasinoSearchService?: boolean;
};

const DEFAULT_PAGE_INDEX = 0;
const DEFAULT_PAGE_SIZE = 10;

const shouldUseMlSearchClient = isMlSearchActive;

type SearchHeadersContext = {
    region: string;
    authToken: string;
    loginToken: string;
};

const buildSearchHeaders = (
    useMlSearch: boolean,
    { region, authToken, loginToken }: SearchHeadersContext,
    legacyApiKey: string
): Record<string, string> => {
    if (useMlSearch) {
        return {
            'x-region-code': region,
            'x-brand': process.env.BRAND as Brand
        };
    }

    return {
        'x-api-key': legacyApiKey,
        'X-Auth-Token': authToken ?? '',
        'X-Login-Token': loginToken ?? ''
    };
};

type SearchParamsInput = {
    query: string;
    platform: string;
    locale: string;
    experimentGroupId: string;
    sessionId: string;
    requestId: string;
    pageIndex?: number;
    pageSize?: number;
};

const buildSearchParams = ({
    query,
    platform,
    locale,
    experimentGroupId,
    sessionId,
    requestId,
    pageIndex = DEFAULT_PAGE_INDEX,
    pageSize = DEFAULT_PAGE_SIZE
}: SearchParamsInput): URLSearchParams => {
    const from = pageIndex * pageSize;
    const normalizedQuery = query.trim();
    const searchParams = new URLSearchParams({
        product: process.env.PRODUCT as string,
        platform,
        query: normalizedQuery,
        from: String(from),
        size: String(pageSize),
        locale
    });

    // Omit unset optional params: legacy accepts missing or empty; ML search errors on empty strings.
    if (experimentGroupId) {
        searchParams.set('experiment_group_id', experimentGroupId);
    }

    if (sessionId) {
        searchParams.set('session_id', sessionId);
    }

    if (requestId) {
        searchParams.set('request_id', requestId);
    }

    return searchParams;
};

const getParams = async (query: string, prismicLocale?: PrismicLocaleCode, options: SearchOptions = {}) => {
    const config = getConfig();

    const [platformCookie, region, authToken, loginToken] = await Promise.all([
        getCookieValue(COOKIES.PLATFORM),
        getCookieValue(COOKIES.REGION),
        getCookieValue(COOKIES.AUTH_TOKEN),
        getCookieValue(COOKIES.LOGIN_TOKEN)
    ]);

    const regionKey = region?.toUpperCase();
    const regionLocale = regionKey && regionKey in LOCALES ? LOCALES[regionKey as Locale] : undefined;
    const locale: string = regionLocale ?? prismicLocale ?? Object.values(LOCALES)[0];
    const platform = platformCookie || DEVICE.DESKTOP;

    const experimentGroupId = options.experimentGroupId ?? '';
    const useMlSearch = shouldUseMlSearchClient(options.hasCasinoSearchService, experimentGroupId);
    const clientConfig = useMlSearch ? config.apis.CasinoMLSearchClient : config.apis.CasinoSearchClient;
    const host = buildApiUrl(clientConfig);

    const headers = buildSearchHeaders(
        useMlSearch,
        { region, authToken, loginToken },
        config.apis.CasinoSearchClient.headers?.['x-api-key'] ?? ''
    );

    const searchParams = buildSearchParams({
        query,
        platform,
        locale,
        experimentGroupId,
        sessionId: options.sessionId ?? '',
        requestId: options.requestId ?? crypto.randomUUID(),
        pageIndex: options.pageIndex,
        pageSize: options.pageSize
    });

    return { host, headers, searchParams };
};

export const searchGames = async (
    query: string,
    prismicLocale?: PrismicLocaleCode,
    options: SearchOptions = {}
): Promise<SearchHits | null> => {
    const { host, headers, searchParams } = await getParams(query, prismicLocale, options);

    try {
        const res = await fetchApi(
            `${host}?${searchParams}`,
            {
                method: 'GET',
                headers
            },
            { excludeCredentials: true } // credentials are built explicitly in `buildSearchHeaders`
        );

        if (!res.ok) {
            return null;
        }

        const data: SearchResponse = await res.json();

        return {
            ...data.body.hits,
            searchEngine: data.searchEngine,
            searchEngineVersion: data.searchEngineVersion,
            hasNextPage: data.metadata.has_next_page
        };
    } catch (error) {
        console.error(`(searchGames) Error searching games: ${(error as Error).message}`);
        return null;
    }
};
